import { createFileRoute } from "@tanstack/react-router";
import { format } from "date-fns";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import ActionButton from "@/components/Button/ActionButton";
import type { Person } from "@/components/Combobox/PersonSelectorCombobox";
import { SimpleDatePicker } from "@/components/DatePicker/SimpleDatePicker";
import { FeedbackDialog } from "@/components/Dialog/FeedbackDialog";
import { Spacer, VStack } from "@/components/Layout";
import { FullScreenOverlay } from "@/components/Loading";
import { WorkerSelect } from "@/components/Select/WorkerSelect";
import { Text } from "@/components/Typography/Text";
import { Separator } from "@/components/ui/separator";
import { toast } from "@/components/ui/use-toast";
import { handleApiError } from "@/lib/api/error";
import { defaultNS } from "@/lib/i18n";
import { useAllowIndividualRequestOperationPackageQuery } from "@/routes/_individual/individual/-query/useAllowIndividualRequestOperationPackageQuery";
import type { OperationPackageType } from "@/routes/_individual/individual/-types/operation-package";

import { useTargetSelection } from "./-hook";
import {
  useGetAllowIndividualRequestOperationPackagePattern,
  usePostSelfServiceStartOperationPackagePattern,
} from "./-query";

export const Route = createFileRoute(
  "/_individual/individual/operation-package/$id/pattern/$patternId/",
)({
  component: RouteComponent,
});

function RouteComponent() {
  const { i18n } = useTranslation();
  const { t } = useTranslation(defaultNS, {
    keyPrefix: "individualPages.operationPackage",
  });

  const { id, patternId } = Route.useParams();
  const navigate = Route.useNavigate();

  const { data: operationPackage } =
    useAllowIndividualRequestOperationPackageQuery(id);
  const { data: operationPackagePattern } =
    useGetAllowIndividualRequestOperationPackagePattern(id, patternId);

  const targetSelection = useTargetSelection(
    operationPackage?.packageType as OperationPackageType,
  );

  const [anchorDate, setAnchorDate] = useState<Date | undefined>(undefined);
  const [selectedTargetId, setSelectedTargetId] = useState<string | undefined>(
    undefined,
  );
  const [isOpen, setIsOpen] = useState(false);

  const { mutate, isPending } = usePostSelfServiceStartOperationPackagePattern(
    id,
    patternId,
    {
      onSuccess: () => {
        setIsOpen(true);
      },
      onError: (error) => {
        const { message } = handleApiError(error, {
          language: i18n.language,
        });
        toast({
          description: message,
          variant: "destructive",
        });
      },
    },
  );

  const handleStartOperationPackageButtonClick = () => {
    if (!anchorDate || (targetSelection?.showSelection && !selectedTargetId))
      return;
    mutate({
      anchorDate: format(anchorDate, "yyyy-MM-dd"),
      targetId: selectedTargetId,
    });
  };

  return (
    <VStack className="py-3 px-5 gap-3 items-start max-w-dialog-lg mx-auto">
      <ActionButton
        icon="ChevronLeft"
        iconColor="muted"
        variant="ghost"
        className="p-0 text-neutral-muted-on-fill font-semibold h-fit"
        onClick={() => {
          navigate({ to: `/individual/operation-package/${id}` });
        }}
      >
        {t("navigateToSelectPattern")}
      </ActionButton>
      <VStack className="gap-2">
        <Text variant="h2">{operationPackage.name}</Text>
        <Text variant="labelLarge">{operationPackagePattern.name}</Text>
      </VStack>
      <div className="py-3 w-full">
        <Separator />
      </div>
      <VStack className="gap-2">
        <div className="text-labelMedium font-semibold text-neutral-muted-on-fill">
          {operationPackagePattern.anchorDateLabel}
        </div>
        <SimpleDatePicker
          value={anchorDate}
          onChange={setAnchorDate}
          disablePast={false}
          showYearMonthSelect={false}
        />
      </VStack>
      <Spacer variant="s" />

      {targetSelection?.showSelection && (
        <>
          {targetSelection?.target === "Person" && (
            <TargetPersonSelection
              onChangeTarget={(id) => setSelectedTargetId(id)}
            />
          )}
          <Spacer variant="s" />
        </>
      )}
      <ActionButton
        onClick={handleStartOperationPackageButtonClick}
        className="w-full"
        disabled={!anchorDate}
      >
        {t("page.startOperationPackage.startOperationPackage", {
          operationPackageName: operationPackage.name,
        })}
      </ActionButton>
      {isPending && <FullScreenOverlay />}
      <FeedbackDialog
        isOpen={isOpen}
        iconName="CircleCheckBigIcon"
        title={t("page.startOperationPackage.processStartedMessage", {
          operationPackageName: operationPackage.name,
        })}
        buttonLabel={t("page.startOperationPackage.goToHome")}
        onClickAction={() => {
          navigate({ to: "/individual" });
        }}
      />
    </VStack>
  );
}

function TargetPersonSelection(p: {
  onChangeTarget: (targetId: string) => void;
}) {
  const { t } = useTranslation(defaultNS, {
    keyPrefix: "individualPages.operationPackage",
  });

  const [selectedTarget, setSelectedTarget] = useState<Person | undefined>(
    undefined,
  );
  const handleChange = (option?: Person) => {
    setSelectedTarget(option);
    p.onChangeTarget(option?.id ?? "");
  };
  return (
    <VStack className="gap-2">
      <div className="text-labelMedium font-semibold text-neutral-muted-on-fill">
        {t("page.startOperationPackage.requestFor")}
      </div>
      <WorkerSelect
        value={selectedTarget}
        onChange={handleChange}
        allowEmpty={false}
      />
    </VStack>
  );
}
